/**
 * ChatContainer - Manages WebSocket connection and state for ChatInterface
 * Handles real-time streaming, message management, and model switching
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChatInterface } from './ChatInterface';
import { ContextWindowDisplay } from './ContextWindowDisplay';
import { SubchatPanel } from './SubchatPanel';
import { wsClient } from '../services/websocket';
import { apiClient } from '../services/api';
import type { Message, Model, WSServerMessage, ContextWindowStatus, Conversation } from '../types';

export interface ChatContainerProps {
  conversationId: string;
  initialMessages?: Message[];
  currentModelId: string;
  models?: Model[];
  onConversationUpdated?: () => void;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
  conversationId,
  initialMessages = [],
  currentModelId,
  models = [],
  onConversationUpdated,
}) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [isStreaming, setIsStreaming] = useState(false);
  const [, setStreamingMessageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [contextStatus, setContextStatus] = useState<ContextWindowStatus | null>(null);
  const [loadingContextStatus, setLoadingContextStatus] = useState(false);
  const [activeSubchat, setActiveSubchat] = useState<{
    subchatId: string;
    sourceExcerpt: string;
  } | null>(null);
  const [subchats, setSubchats] = useState<Conversation[]>([]);
  // Use ref to track the current streaming message content
  const streamingContentRef = useRef<string>('');

  // Fetch the parent's existing subchats so past branches can be reopened.
  // Re-fetched after creating a new one, and whenever the parent conversation changes.
  const fetchSubchats = useCallback(async () => {
    if (!conversationId) {
      setSubchats([]);
      return;
    }
    try {
      const result = await apiClient.getSubchats(conversationId);
      setSubchats(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Failed to fetch subchats:', err);
      setSubchats([]);
    }
  }, [conversationId]);

  useEffect(() => {
    fetchSubchats();
  }, [fetchSubchats]);

  // Group subchats by the parent message they branched from, for the pill in Message.tsx
  const subchatsByMessageId = React.useMemo(() => {
    const map = new Map<string, Conversation[]>();
    for (const subchat of subchats) {
      if (!subchat.parentMessageId) continue;
      const existing = map.get(subchat.parentMessageId) ?? [];
      existing.push(subchat);
      map.set(subchat.parentMessageId, existing);
    }
    return map;
  }, [subchats]);

  // Sync messages when initialMessages changes (e.g., when loading a different conversation)
  useEffect(() => {
    console.log('[ChatContainer] Updating messages from initialMessages, count:', initialMessages.length);
    setMessages(initialMessages);
  }, [initialMessages, conversationId]);

  // Connect to WebSocket on mount
  useEffect(() => {
    wsClient.connect();

    return () => {
      // Cleanup: disconnect on unmount
      wsClient.disconnect();
    };
  }, []);

  // Fetch context window status when messages change or model changes
  useEffect(() => {
    const fetchContextStatus = async () => {
      // Find the current model to get its context window size
      const currentModel = models.find(m => m.id === currentModelId);
      if (!currentModel || !conversationId) {
        return;
      }

      setLoadingContextStatus(true);
      try {
        const status = await apiClient.getContextWindowStatus(
          conversationId,
          currentModel.contextWindow
        );
        setContextStatus(status);
      } catch (err) {
        console.error('Failed to fetch context window status:', err);
        // Don't show error to user, just log it
      } finally {
        setLoadingContextStatus(false);
      }
    };

    fetchContextStatus();
  }, [conversationId, currentModelId, messages.length, models]);



  // Handle incoming tokens during streaming
  const handleTokenReceived = useCallback((token: string) => {
    streamingContentRef.current += token;

    // Update the streaming message in the messages array
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];

      // If last message is the streaming message, update it
      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.id.startsWith('streaming-')) {
        return [
          ...prevMessages.slice(0, -1),
          {
            ...lastMessage,
            content: streamingContentRef.current,
          },
        ];
      }

      // Otherwise, create a new streaming message
      const newMessage: Message = {
        id: `streaming-${Date.now()}`,
        role: 'assistant',
        content: streamingContentRef.current,
        modelId: currentModelId,
        timestamp: new Date().toISOString(),
        tokenCount: 0, // Will be updated on completion
      };

      setStreamingMessageId(newMessage.id);
      return [...prevMessages, newMessage];
    });
  }, [currentModelId]);

  // Handle stream completion
  const handleStreamComplete = useCallback((messageId: string, tokenCount: number) => {
    // Capture the content before clearing
    const finalContent = streamingContentRef.current;

    setIsStreaming(false);

    // Update the message with final ID and token count, preserving content
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];

      if (lastMessage && lastMessage.role === 'assistant') {
        return [
          ...prevMessages.slice(0, -1),
          {
            ...lastMessage,
            id: messageId,
            tokenCount,
            content: finalContent, // Use the captured content
          },
        ];
      }

      return prevMessages;
    });

    // Clear streaming state after updating messages
    setStreamingMessageId(null);
    streamingContentRef.current = '';
    setError(null);

    // Notify parent that conversation has been updated
    if (onConversationUpdated) {
      onConversationUpdated();
    }
  }, [onConversationUpdated]);

  // Handle stream errors
  const handleStreamError = useCallback((errorMessage: string, code: string) => {
    setIsStreaming(false);
    setStreamingMessageId(null);
    streamingContentRef.current = '';

    setError(`Error (${code}): ${errorMessage}`);

    // Remove the incomplete streaming message if it exists
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];

      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.id.startsWith('streaming-')) {
        return prevMessages.slice(0, -1);
      }

      return prevMessages;
    });
  }, []);

  const handleModelSwitched = useCallback((modelId: string) => {
    console.log('[ChatContainer] Model switched confirmed by server:', modelId);
  }, []);

  // Handle WebSocket messages
  useEffect(() => {
    const handleMessage = (message: WSServerMessage) => {
      // Only handle messages for this conversation
      if ('conversationId' in message && message.conversationId !== conversationId) {
        return;
      }

      switch (message.type) {
        case 'token':
          handleTokenReceived(message.token);
          break;

        case 'complete':
          handleStreamComplete(message.messageId, message.tokenCount);
          break;

        case 'error':
          handleStreamError(message.error, message.code);
          break;

        case 'model_switched':
          handleModelSwitched(message.modelId);
          break;

        default:
          break;
      }
    };

    const unsubscribe = wsClient.onMessage(handleMessage);

    return () => {
      unsubscribe();
    };
  }, [conversationId, handleTokenReceived, handleStreamComplete, handleStreamError, handleModelSwitched]);

  // Send a new message
  const handleSendMessage = useCallback((content: string) => {
    if (!content.trim() || isStreaming) {
      return;
    }

    // Add user message to the UI immediately
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      modelId: currentModelId,
      timestamp: new Date().toISOString(),
      tokenCount: 0, // Will be calculated by backend
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsStreaming(true);
    setError(null);
    streamingContentRef.current = '';

    // Send message via WebSocket
    try {
      wsClient.sendMessage(conversationId, content, currentModelId);
    } catch (err) {
      setIsStreaming(false);
      setError(err instanceof Error ? err.message : 'Failed to send message');
    }
  }, [conversationId, currentModelId, isStreaming]);

  // Cancel streaming
  const handleCancelStream = useCallback(() => {
    if (!isStreaming) {
      return;
    }

    try {
      wsClient.cancelStream(conversationId);
      setIsStreaming(false);
      setStreamingMessageId(null);
      streamingContentRef.current = '';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel stream');
    }
  }, [conversationId, isStreaming]);

  // Handle context truncation
  const handleTruncate = useCallback(() => {
    // For now, just show an alert. In a full implementation, this would call
    // a backend endpoint to truncate older messages
    alert('Context truncation would remove older messages to fit within the context window. This feature requires backend implementation.');
  }, []);

  // Branch a subchat from a text selection in an assistant message
  const handleBranchFromSelection = useCallback(
    async (messageId: string, excerpt: string) => {
      try {
        const subchat = await apiClient.createSubchat(conversationId, messageId, excerpt, currentModelId);
        setActiveSubchat({ subchatId: subchat.id, sourceExcerpt: excerpt });
        fetchSubchats();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create subchat');
      }
    },
    [conversationId, currentModelId, fetchSubchats]
  );

  // Reopen a previously created subchat from its pointer on the parent message
  const handleOpenSubchat = useCallback((subchat: Conversation) => {
    setActiveSubchat({ subchatId: subchat.id, sourceExcerpt: subchat.sourceExcerpt ?? '' });
  }, []);

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="mx-4 mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg text-sm flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <ChatInterface
          conversationId={conversationId}
          messages={messages}
          currentModelId={currentModelId}
          isStreaming={isStreaming}
          onSendMessage={handleSendMessage}
          onCancelStream={handleCancelStream}
          models={models}
          onBranchFromSelection={handleBranchFromSelection}
          subchatsByMessageId={subchatsByMessageId}
          onOpenSubchat={handleOpenSubchat}
        />
      </div>

      {activeSubchat && (
        <SubchatPanel
          subchatConversationId={activeSubchat.subchatId}
          sourceExcerpt={activeSubchat.sourceExcerpt}
          modelId={currentModelId}
          models={models}
          onClose={() => setActiveSubchat(null)}
        />
      )}

      <ContextWindowDisplay
        status={contextStatus}
        onTruncate={handleTruncate}
        loading={loadingContextStatus}
      />
    </div>
  );
};
